import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
  useFormChangeTracking,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  SearchDropdown,
  type SearchDropdownItem,
} from '@/components/ui/search-dropdown'
import { integrationsClient } from '@/lib/api/integrations/integrations.client'
import { mapAssigneeToItem } from '@/lib/api/integrations/hubspot.utils'
import { toast } from 'sonner'
import { FloatingActionIslandContent } from '@/components/ui/FloatingActionIslandContent'
import { Button } from '@/components/ui/button'
import { INTEGRATIONS } from '@super/shared-types'
import { useAppAuth } from '@/contexts/app-auth.context'
import { useAuth } from '@/hooks/use-auth'
import { Heading } from '@/components/ui/typography'

const formSchema = z.object({
  clientSecret: z.string(),
  defaultTaskAssigneeId: z.string(),
  defaultRequestedByUserId: z.string(),
  defaultVendorId: z.string(),
})

type FormValues = z.infer<typeof formSchema>

const SAVED_SECRET_PLACEHOLDER = '••••••••'

export interface DoorLoopConnectFormProps {
  isEditMode?: boolean
  onConnected: () => void
}

export function DoorLoopConnectForm({
  isEditMode = false,
  onConnected,
}: DoorLoopConnectFormProps) {
  const { currentCompany } = useAppAuth()
  const { isSuperAdmin } = useAuth()
  const [isLoading, setIsLoading] = useState(isEditMode)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [userItems, setUserItems] = useState<SearchDropdownItem<string>[]>([])
  const [vendorItems, setVendorItems] = useState<SearchDropdownItem<string>[]>(
    [],
  )
  const [isLoadingOptions, setIsLoadingOptions] = useState(isEditMode)
  const [optionsFailed, setOptionsFailed] = useState(false)
  // Full stored metadata blob — the update endpoint replaces metadata
  // wholesale, so unknown keys must be carried through on save.
  const [existingMetadata, setExistingMetadata] = useState<
    Record<string, unknown>
  >({})

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      clientSecret: '',
      defaultTaskAssigneeId: '',
      defaultRequestedByUserId: '',
      defaultVendorId: '',
    },
  })

  useEffect(() => {
    if (!isEditMode || !currentCompany?.id) {
      setIsLoading(false)
      return
    }

    const fetchStatus = async () => {
      try {
        setIsLoading(true)
        const integrations = isSuperAdmin
          ? await integrationsClient.getAllAdminIntegrationsStatus(
              currentCompany.id,
            )
          : await integrationsClient.getAllIntegrationsStatus()
        const doorloop = integrations.find(
          i => i.type === INTEGRATIONS.DOORLOOP.TYPE,
        )
        if (doorloop?.connected) {
          const meta = doorloop.metadata ?? {}
          setExistingMetadata(meta)
          form.reset({
            clientSecret: SAVED_SECRET_PLACEHOLDER,
            defaultTaskAssigneeId: String(meta.defaultTaskAssigneeId ?? ''),
            defaultRequestedByUserId: String(
              meta.defaultRequestedByUserId ?? '',
            ),
            defaultVendorId: String(meta.defaultVendorId ?? ''),
          })
        }
      } catch (error) {
        console.error('Error loading DoorLoop configuration:', error)
        toast.error('Failed to load existing DoorLoop configuration')
      } finally {
        setIsLoading(false)
      }
    }
    fetchStatus()
  }, [isEditMode, currentCompany?.id, isSuperAdmin, form])

  useEffect(() => {
    if (!isEditMode || !currentCompany?.id) return

    const controller = new AbortController()
    const fetchOptions = async () => {
      try {
        setIsLoadingOptions(true)
        setOptionsFailed(false)
        const [users, vendors] = await Promise.all([
          isSuperAdmin
            ? integrationsClient.doorLoop.getAdminUsers(currentCompany.id)
            : integrationsClient.doorLoop.getUsers(),
          isSuperAdmin
            ? integrationsClient.doorLoop.getAdminVendors(currentCompany.id)
            : integrationsClient.doorLoop.getVendors(),
        ])
        if (controller.signal.aborted) return
        setUserItems(users.map(mapAssigneeToItem))
        setVendorItems(vendors.map(mapAssigneeToItem))
      } catch (error) {
        if (controller.signal.aborted) return
        console.error('Error fetching DoorLoop users/vendors:', error)
        setOptionsFailed(true)
        toast.error('Unable to fetch users and vendors from DoorLoop.')
      } finally {
        if (!controller.signal.aborted) setIsLoadingOptions(false)
      }
    }
    fetchOptions()
    return () => controller.abort()
  }, [isEditMode, currentCompany?.id, isSuperAdmin])

  const handleSubmit = async (values: FormValues) => {
    if (!currentCompany?.id) {
      toast.error('No company selected')
      return
    }

    const secretUnchanged =
      isEditMode &&
      (values.clientSecret === SAVED_SECRET_PLACEHOLDER ||
        values.clientSecret === '')

    if (!isEditMode && !values.clientSecret.trim()) {
      toast.error('API key is required when connecting')
      return
    }

    try {
      setIsSubmitting(true)

      if (!secretUnchanged) {
        if (isSuperAdmin) {
          await integrationsClient.doorLoop.connectAdmin(
            currentCompany.id,
            values.clientSecret,
          )
        } else {
          await integrationsClient.doorLoop.connect(values.clientSecret)
        }
      }

      if (isEditMode) {
        const metadata = {
          ...existingMetadata,
          defaultTaskAssigneeId: values.defaultTaskAssigneeId || undefined,
          defaultRequestedByUserId:
            values.defaultRequestedByUserId || undefined,
          defaultVendorId: values.defaultVendorId || undefined,
        }
        if (isSuperAdmin) {
          await integrationsClient.doorLoop.updateAdminMetadata(
            currentCompany.id,
            metadata,
          )
        } else {
          await integrationsClient.doorLoop.updateMetadata(metadata)
        }
        toast.success('DoorLoop configuration updated successfully!')
      } else {
        toast.success('DoorLoop integration connected successfully!', {
          description:
            'We are syncing your contacts, properties, and units now.',
        })
      }
      onConnected()
    } catch (error) {
      console.error(
        `Error ${isEditMode ? 'updating' : 'connecting'} DoorLoop:`,
        error,
      )
      toast.error(
        error instanceof Error
          ? error.message
          : `Failed to ${isEditMode ? 'update' : 'connect'} DoorLoop integration`,
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const dropdownFields: {
    name: keyof Pick<
      FormValues,
      'defaultTaskAssigneeId' | 'defaultRequestedByUserId' | 'defaultVendorId'
    >
    label: string
    description: string
    items: SearchDropdownItem<string>[]
    placeholder: string
  }[] = [
    {
      name: 'defaultTaskAssigneeId',
      label: 'Default task assignee',
      description:
        'Tasks created from handoffs are assigned to this DoorLoop user by default.',
      items: userItems,
      placeholder: 'Select assignee...',
    },
    {
      name: 'defaultRequestedByUserId',
      label: 'Default requested-by user',
      description:
        'Recorded in DoorLoop as who requested internal tasks when the handoff rule does not set one.',
      items: userItems,
      placeholder: 'Select user...',
    },
    {
      name: 'defaultVendorId',
      label: 'Default work-order vendor',
      description:
        'Work orders are assigned to this vendor when the handoff rule does not set one.',
      items: vendorItems,
      placeholder: 'Select vendor...',
    },
  ]

  const FloatingIslandContent = () => {
    const { hasUnsavedChanges } = useFormChangeTracking()

    return (
      <FloatingActionIslandContent
        show={hasUnsavedChanges}
        componentId="doorloop-form"
        priority={20}
      >
        <div className="inline-flex w-full max-w-2xl">
          <div className="flex flex-grow items-center space-x-2 text-sm text-gray-600 whitespace-nowrap pointer-events-none mr-4">
            <span>
              {isEditMode
                ? 'Update your API key and task defaults for DoorLoop'
                : 'Connect your API key for DoorLoop'}
            </span>
          </div>
          <div className="flex items-center space-x-2 justify-end pointer-events-auto ml-auto">
            <Button
              onClick={form.handleSubmit(handleSubmit)}
              disabled={isSubmitting}
              size="sm"
              variant="form"
            >
              <span>
                {isSubmitting
                  ? isEditMode
                    ? 'Updating...'
                    : 'Connecting...'
                  : isEditMode
                    ? 'Update'
                    : 'Connect'}
              </span>
            </Button>
          </div>
        </div>
      </FloatingActionIslandContent>
    )
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-20 bg-secondary/50 animate-pulse rounded" />
        <div className="h-20 bg-secondary/50 animate-pulse rounded" />
        <div className="h-20 bg-secondary/50 animate-pulse rounded" />
      </div>
    )
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        noValidate
        className="space-y-6 max-w-2xl"
      >
        <FormField
          control={form.control}
          name="clientSecret"
          render={({ field }) => (
            <FormItem className="max-w-md">
              <FormLabel>API Key</FormLabel>
              <FormControl>
                <Input
                  type="password"
                  placeholder="Your DoorLoop API key"
                  disabled={isSubmitting}
                  autoFocus={!isEditMode}
                  {...field}
                />
              </FormControl>
              <FormDescription>
                {isEditMode
                  ? 'Leave unchanged to keep the existing key. Enter a new key to replace it.'
                  : 'Create an API key in DoorLoop under Settings → Zapier & API Keys. Requires a DoorLoop Premium plan.'}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        {isEditMode && (
          <>
            <Heading variant="subsection-title">Task defaults</Heading>

            {dropdownFields.map(fieldConfig => (
              <FormField
                key={fieldConfig.name}
                control={form.control}
                name={fieldConfig.name}
                render={({ field }) => {
                  const selected =
                    fieldConfig.items.find(item => item.id === field.value) ??
                    null
                  return (
                    <FormItem className="max-w-md">
                      <FormLabel>{fieldConfig.label}</FormLabel>
                      <FormControl>
                        <SearchDropdown<string>
                          items={fieldConfig.items}
                          selectedItems={selected}
                          onSelect={item => {
                            const sel = Array.isArray(item) ? item[0] : item
                            field.onChange(
                              sel?.data != null ? String(sel.data) : '',
                            )
                          }}
                          buttonLabel={
                            selected?.label ?? fieldConfig.placeholder
                          }
                          placeholder={fieldConfig.placeholder}
                          searchPlaceholder="Search by name or email..."
                          emptyMessage={
                            optionsFailed
                              ? 'Could not load options'
                              : 'No results found'
                          }
                          buttonClassName="w-full [&_.truncate]:ml-0"
                          disabled={isSubmitting || isLoadingOptions}
                        />
                      </FormControl>
                      <FormDescription>
                        {fieldConfig.description}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )
                }}
              />
            ))}
          </>
        )}
      </form>

      <FloatingIslandContent />
    </Form>
  )
}
